import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { Segment } from '@feathr/blackbox';
import type { IToastAction } from '@feathr/components';
import { useExportModal } from '@feathr/extender/hooks/useExportModal';
import { useFlags, useLocalUrl } from '@feathr/extender/state';

import type { IUseEmailActivityTabReturn } from './EmailActivityTab/EmailActivityTab.hooks';
import { useEmailActivityTab } from './EmailActivityTab/EmailActivityTab.hooks';
import type {
  IGroupExportTabHook,
  ISharedFormSlice,
  TGroupExportTab,
} from './GroupExportModal.types';
import type {
  IEmailActivityExportPayload,
  IOtherActivitiesExportPayload,
} from './GroupExportModal.utils';
import { resolveExportNameTemplate } from './GroupExportModal.utils';
import type { IUseOtherActivitiesTabReturn } from './OtherActivitiesTab/OtherActivitiesTab.hooks';
import { useOtherActivitiesTab } from './OtherActivitiesTab/OtherActivitiesTab.hooks';
import type { IUsePersonTabReturn } from './PersonTab/PersonTab.hooks';
import { usePersonTab } from './PersonTab/PersonTab.hooks';
import type { IUseSimpleExportFormReturn } from './SimpleExportForm/SimpleExportForm.hooks';
import { useSimpleExportForm } from './SimpleExportForm/SimpleExportForm.hooks';

export type { TGroupExportTab } from './GroupExportModal.types';

const EMAIL_ACTIVITY_PRESET_IDS = ['30d', '60d', '90d'];
const EMAIL_ACTIVITY_DEFAULT_DATE_RANGE = '30d';

export interface IUseGroupExportModalOptions {
  segment: Segment;
  onClose: () => void;
  /** Overrides the live export call; the modal wires the real one when omitted. */
  onExportEmailActivity?: (payload: IEmailActivityExportPayload) => Promise<void>;
  /** Overrides the live export call; the modal wires the real one when omitted. */
  onExportOtherActivities?: (payload: IOtherActivitiesExportPayload) => Promise<void>;
  successToastAction?: IToastAction;
}

export interface IUseGroupExportModalReturn {
  activeTab: TGroupExportTab;
  setActiveTab: (tab: TGroupExportTab) => void;
  isLoading: boolean;
  isSubmitDisabled: boolean;
  personTab: IUsePersonTabReturn;
  emailActivityTab: IUseEmailActivityTabReturn;
  otherActivitiesTab: IUseOtherActivitiesTabReturn;
  simpleExportForm: IUseSimpleExportFormReturn;
  handleSubmit: () => Promise<void>;
  handleClose: () => void;
}

export function useGroupExportModal({
  segment,
  onClose,
  onExportEmailActivity,
  onExportOtherActivities,
  successToastAction,
}: IUseGroupExportModalOptions): IUseGroupExportModalReturn {
  const { t } = useTranslation();
  const { showEmailActivityExport } = useFlags();
  const history = useHistory();
  const localUrl = useLocalUrl();

  // Reactive read so MobX re-renders once the segment's name loads.
  const resolvedExportName = resolveExportNameTemplate(segment.get('name', ''));

  const viewExportToastAction = useMemo<IToastAction>(
    () => ({
      label: t('View export'),
      onClick: (): void => history.push(localUrl('/data/exports')),
    }),
    [history, localUrl, t],
  );

  const exportEmailActivity = useCallback(
    async (payload: IEmailActivityExportPayload): Promise<void> => {
      await segment.exportEmailActivity(payload);
    },
    [segment],
  );

  const exportOtherActivities = useCallback(
    async (payload: IOtherActivitiesExportPayload): Promise<void> => {
      await segment.exportOtherActivities(payload);
    },
    [segment],
  );

  const sharedExport = useExportModal(t, {
    initialExportName: resolvedExportName,
    presetIds: EMAIL_ACTIVITY_PRESET_IDS,
    defaultDateRange: EMAIL_ACTIVITY_DEFAULT_DATE_RANGE,
  });

  const [activeTab, setActiveTab] = useState<TGroupExportTab>('person');

  const sharedFormSlice: ISharedFormSlice = {
    exportName: sharedExport.exportName,
    exportNameHadFocus: sharedExport.exportNameHadFocus,
    exportNameError: sharedExport.validationErrors?.exportName,
    handleChangeExportName: sharedExport.handleChangeExportName,
    handleExportNameBlur: sharedExport.handleExportNameBlur,
    email: sharedExport.email,
    emailHadFocus: sharedExport.emailHadFocus,
    emailError: sharedExport.validationErrors?.email,
    handleChangeEmail: sharedExport.handleChangeEmail,
    handleEmailBlur: sharedExport.handleEmailBlur,
  };

  const personTab = usePersonTab({
    segment,
    form: sharedFormSlice,
    handleExport: sharedExport.handleExport,
  });

  const dateRangeSection = {
    selectedDateRange: sharedExport.selectedDateRange,
    customDateRange: sharedExport.customDateRange,
    dateRangeHadFocus: sharedExport.dateRangeHadFocus,
    dateRangeOptions: sharedExport.dateRangeOptions,
    dateRangeError: sharedExport.validationErrors?.dateRange,
    handleDateRangeChange: sharedExport.handleDateRangeChange,
    handleCustomDateRangeChange: sharedExport.handleCustomDateRangeChange,
  };

  const emailActivityTab = useEmailActivityTab({
    form: sharedFormSlice,
    dateRange: dateRangeSection,
    resolveDateRange: sharedExport.resolveDateRange,
    handleExport: sharedExport.handleExport,
    onExportEmailActivity: onExportEmailActivity ?? exportEmailActivity,
    successToastAction: successToastAction ?? viewExportToastAction,
  });

  const otherActivitiesTab = useOtherActivitiesTab({
    form: sharedFormSlice,
    dateRange: dateRangeSection,
    resolveDateRange: sharedExport.resolveDateRange,
    handleExport: sharedExport.handleExport,
    onExportOtherActivities: onExportOtherActivities ?? exportOtherActivities,
    successToastAction: successToastAction ?? viewExportToastAction,
  });

  // Flag-off form; carries the People/Activity export-type toggle the legacy modal had.
  const simpleExportForm = useSimpleExportForm({
    segment,
    form: sharedFormSlice,
    handleExport: sharedExport.handleExport,
  });

  const handleClose = useCallback((): void => {
    sharedExport.resetFormState();
    personTab.resetState();
    emailActivityTab.resetState();
    otherActivitiesTab.resetState();
    simpleExportForm.resetState();
    setActiveTab('person');
    onClose();
  }, [
    emailActivityTab.resetState,
    onClose,
    otherActivitiesTab.resetState,
    personTab.resetState,
    sharedExport.resetFormState,
    simpleExportForm.resetState,
  ]);

  // Record lookup so adding a new TGroupExportTab id raises a TS error here.
  const tabStatesByTab: Record<TGroupExportTab, IGroupExportTabHook<unknown> | undefined> = {
    person: personTab,
    emailActivity: emailActivityTab,
    otherActivities: otherActivitiesTab,
  };
  // With the flag off there are no tabs; the single-pane form is the active surface.
  const activeTabState = showEmailActivityExport ? tabStatesByTab[activeTab] : simpleExportForm;

  const isSubmitDisabled =
    sharedExport.isLoading ||
    activeTabState === undefined ||
    activeTabState.validationErrors.length > 0;

  const handleSubmit = useCallback(async (): Promise<void> => {
    const didSucceed = (await activeTabState?.handleSubmit()) ?? false;
    if (didSucceed) {
      handleClose();
    }
  }, [activeTabState, handleClose]);

  return {
    activeTab,
    setActiveTab,
    isLoading: sharedExport.isLoading,
    isSubmitDisabled,
    personTab,
    emailActivityTab,
    otherActivitiesTab,
    simpleExportForm,
    handleSubmit,
    handleClose,
  };
}
